import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Truck, Leaf, Clock, Shield, Users, Phone, Mail, MapPin, 
  ChevronRight, Check, Handshake, Target, FileCheck, Heart,
  DollarSign, ArrowRight, Menu, X, Instagram, MessageCircle, Linkedin, ShoppingBag, Star
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import FloatingWhatsapp from '../components/FloatingWhatsapp';
import RequestQuoteDialog from '../components/RequestQuoteDialog';
import BisonFeedCarousel from '../components/BisonFeedCarousel';
import { organizationJsonLd, localBusinessJsonLd, INDUSTRIES, PRODUCT_CATEGORIES, COMPANY } from '../content/seo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [media, setMedia] = useState({});
  const [landingSettings, setLandingSettings] = useState({ reviews_display_enabled: false, linkedin_url: '', instagram_url: '', linkedin_posts: [], instagram_posts: [] });
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: '', email: '', rating: 5, comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/landing/media/public`).then(r => setMedia(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/settings/public`).then(r => setLandingSettings(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/reviews/public`).then(r => { if (r.data.enabled) setReviews(r.data.reviews); }).catch(() => {});
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/landing/messages`, contactForm);
      toast.success('Thank you! Your message has been received.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) { toast.error('Please fill in your name and review'); return; }
    setReviewSubmitting(true);
    try {
      const payload = { ...reviewForm };
      if (!payload.email) delete payload.email;
      await axios.post(`${API_URL}/landing/reviews`, payload);
      toast.success("Thanks for the feedback! Your review will appear once we've reviewed it.");
      setReviewForm({ name: '', email: '', rating: 5, comment: '' });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setReviewSubmitting(false); }
  };

  const heroImg = media.hero_image?.image_url;
  const aboutImg = media.about_image?.image_url;
  const contactImg = media.contact_image?.image_url;
  const socialHasContent = landingSettings.linkedin_url || landingSettings.instagram_url || (landingSettings.linkedin_posts || []).length > 0 || (landingSettings.instagram_posts || []).length > 0;

  const offerings = [
    {
      icon: Leaf,
      title: 'Fresh Fruits & Vegetables',
      description: 'Farm-fresh, seasonal produce sourced locally for maximum freshness, flavor, and nutritional value.',
      image: '/images/new/picture6.jpg'
    },
    {
      icon: Truck,
      title: 'Fresh Meat & Poultry',
      description: 'Premium meats and poultry from trusted suppliers, handled under strict quality standards.',
      image: '/images/manfredrichter-chicken-3212144_1280.jpg'
    },
    {
      icon: Users,
      title: 'Custom Orders & Bulk Deliveries',
      description: 'Flexible ordering options and bulk delivery services tailored to your specific requirements.',
      image: 'https://customer-assets.emergentagent.com/job_bison-grocery-1/artifacts/5yf0wln5_Truck%20image.png'
    },
    {
      icon: Clock,
      title: 'Scheduled Home Delivery',
      description: 'Convenient and reliable delivery of fresh produce directly to your doorstep — on time, every time.',
      image: '/images/fortytwo-qRSntl3zxEk-unsplash.jpg'
    }
  ];

  const strengths = [
    { icon: Leaf, title: 'Freshness Guaranteed', description: 'Direct sourcing from trusted farmers for true farm-to-table reliability.' },
    { icon: Handshake, title: 'Strong Partnerships', description: 'Building sustainable, long-term relationships across the supply chain.' },
    { icon: Clock, title: 'Reliable & Timely Delivery', description: 'Efficient logistics ensuring your business runs smoothly without interruption.' },
    { icon: Target, title: 'Customized Solutions', description: 'Tailored supply solutions designed to meet specific operational needs.' },
    { icon: DollarSign, title: 'Competitive & Fair Pricing', description: 'Premium-quality products at competitive and sustainable pricing.' },
    { icon: Shield, title: 'Integrity & Transparency', description: 'Honesty, accountability, and transparency in every transaction.' },
    { icon: Heart, title: 'Customer Commitment', description: 'Your success is our priority with responsive service and personalized support.' },
    { icon: FileCheck, title: 'Compliance & Quality Assurance', description: 'Strict adherence to Rwandan laws and food safety standards.' }
  ];

  const navLinks = [
    { label: 'About Us', id: 'about' },
    { label: 'What We Offer', id: 'offerings' },
    { label: 'Our Strengths', id: 'strengths' }
  ];

  return (
    <>
    <SEOHead
      title=""
      description={`${COMPANY.name} — ${COMPANY.description}`}
      path=""
      jsonLd={[organizationJsonLd(), localBusinessJsonLd(), {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: COMPANY.name,
        url: COMPANY.site,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${COMPANY.site}/products?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      }]}
    />
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-black/5 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => scrollToSection('hero')} className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
              <img 
                src="/bison-logo.png" 
                alt="Bison Food Solutions" 
                className="h-14 w-auto"
              />
              <div className="hidden sm:block text-left">
                <h1 className="text-lg font-heading font-bold text-[#22c55e]">Bison Food Solutions</h1>
                <p className="text-xs text-muted-foreground">Fresh, Reliable & Farmer-supported</p>
              </div>
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-12">
              {navLinks.map(link => (
                <button 
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-sm font-medium text-foreground/80 hover:text-[#22c55e] transition-colors whitespace-nowrap"
                >
                  {link.label}
                </button>
              ))}
              <Button 
                onClick={() => scrollToSection('contact')}
                variant="outline"
                className="border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10 rounded-full px-6"
              >
                Contact Us
              </Button>
              <a href="/shop" data-testid="landing-shop-cta">
                <Button className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-full px-6 gap-1">
                  <ShoppingBag className="w-4 h-4" /> Shop Online
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-border">
              <div className="flex flex-col space-y-2 pt-4">
                {navLinks.map(link => (
                  <button 
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left px-4 py-2 text-sm font-medium text-foreground/80 hover:text-[#22c55e] hover:bg-muted/50 rounded-lg"
                  >
                    {link.label}
                  </button>
                ))}
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-left px-4 py-2 text-sm font-medium text-[#22c55e] hover:bg-muted/50 rounded-lg"
                >
                  Contact Us
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative pt-20 min-h-[92vh] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[10s] scale-105 hover:scale-100"
          style={{ 
            backgroundImage: `url(${heroImg || '/images/new/picture4.jpg'})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/85 via-black/60 to-black/20"></div>
          <div className="absolute inset-0 noise-bg opacity-40 pointer-events-none"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-20">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-1.5 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-sm font-medium animate-fade-in-up">Kigali · since 2022</span>
            <h1 className="font-heading font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-[1.05] text-balance animate-fade-in-up delay-100">
              Kigali fresh produce, <span className="italic text-[#a7f3d0]">delivered.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/85 max-w-xl leading-relaxed animate-fade-in-up delay-200">
              Fruits, vegetables, meats and poultry — hand-picked for hotels, restaurants, and households across Rwanda. Quality you can build a menu around.
            </p>
            <p className="mt-4 text-base text-[#a7f3d0] font-medium italic animate-fade-in-up delay-300">
              "We don't just supply food — we build partnerships."
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-in-up delay-500">
              <Button
                onClick={() => setQuoteOpen(true)}
                size="lg"
                data-testid="hero-request-quote-cta"
                className="h-12 px-8 rounded-full bg-amber-400 text-emerald-900 hover:bg-amber-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/30 transition-all gap-2 font-semibold"
              >
                Request a Quote <ArrowRight className="w-5 h-5" />
              </Button>
              <a href="/shop" data-testid="hero-shop-online-cta">
                <Button 
                  size="lg"
                  className="h-12 px-8 rounded-full bg-white text-primary hover:bg-white hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/30 transition-all gap-2 font-medium"
                >
                  <ShoppingBag className="w-5 h-5" /> Shop online
                </Button>
              </a>
              <Button 
                onClick={() => scrollToSection('offerings')}
                size="lg"
                variant="outline"
                className="h-12 px-8 rounded-full bg-transparent border-white/40 text-white hover:bg-white/10 hover:-translate-y-1 transition-all"
              >
                Explore our produce
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">About Us</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-foreground">
                About Bison Food Solutions Ltd.
              </h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Bison Food Solutions Ltd. is a trusted supplier of premium fresh groceries, offering a wide selection 
                of high-quality fruits, vegetables, meats, and poultry. We proudly serve hotels, restaurants, bars, 
                and households with products carefully sourced for freshness, safety, and consistency.
              </p>
              
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-primary/5 p-5 rounded-xl">
                  <h3 className="font-semibold text-foreground">Our Vision</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    To lead the fresh food distribution industry by setting the standard for quality, reliability, 
                    and customer partnership.
                  </p>
                </div>
                <div className="bg-primary/5 p-5 rounded-xl">
                  <h3 className="font-semibold text-foreground">Our Mission</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    To supply high-quality fresh produce sourced directly from farmers, delivered with consistency, 
                    efficiency, and integrity.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src={aboutImg || "/images/new/picture5.jpg"}
                alt="Fresh produce market"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">EST. 2022</div>
                <div className="text-sm opacity-90">Serving Rwanda</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section id="offerings" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-primary font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-foreground">
              What We Offer
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Quality products and services tailored to meet your needs
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            {offerings.map((item, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="h-56 overflow-hidden">
                  <img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Strengths Section */}
      <section id="strengths" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-primary font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-foreground">
              Our Strengths & Core Values
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Our competitive advantage is built on strong values, operational excellence, and full compliance with regulatory standards.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {strengths.map((item, index) => (
              <div 
                key={index}
                className="bg-muted/30 hover:bg-primary/5 p-6 rounded-xl transition-colors group"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#22c55e]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white">
            Ready to Partner with Us?
          </h2>
          <p className="mt-4 text-lg text-white/90 max-w-2xl mx-auto">
            Experience the difference of working with a dedicated food supply partner committed to your success.
          </p>
          <Button 
            onClick={() => scrollToSection('contact')}
            size="lg"
            className="mt-8 bg-white text-[#22c55e] hover:bg-white/90 rounded-full px-8"
          >
            Get in Touch
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-muted/40 relative overflow-hidden">
        <div className="absolute inset-0 noise-bg opacity-30 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <span className="text-primary font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-foreground">
                Contact Us
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Ready to experience premium quality groceries? Get in touch with us today and let us be your trusted supply partner.
              </p>

              <div className="mt-8 space-y-6">
                <a href="tel:+250791687658" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Call Us</div>
                    <div className="font-semibold text-foreground">+250 791 687 658</div>
                  </div>
                </a>

                <a href="mailto:sales@bison.rw" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Email Us</div>
                    <div className="font-semibold text-foreground">sales@bison.rw</div>
                  </div>
                </a>

                <a href="https://www.instagram.com/bison_food_solutions_ltd/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 bg-[#E1306C]/10 rounded-full flex items-center justify-center">
                    <Instagram className="w-5 h-5 text-[#E1306C]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Follow Us</div>
                    <div className="font-semibold text-foreground">@bison_food_solutions_ltd</div>
                  </div>
                </a>

                <a href="https://wa.me/250791687658" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 bg-[#25D366]/10 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">WhatsApp</div>
                    <div className="font-semibold text-foreground">+250 791 687 658</div>
                  </div>
                </a>

                <a href="https://rw.linkedin.com/company/bison-food-solutions-ltd?trk=public_post_feed-actor-name" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="w-12 h-12 bg-[#0A66C2]/10 rounded-full flex items-center justify-center">
                    <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">LinkedIn</div>
                    <div className="font-semibold text-foreground">Bison Food Solutions Ltd</div>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 bg-[#22c55e]/10 rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Visit Us</div>
                    <div className="font-semibold text-foreground">Kigali, Rwanda</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-xl font-semibold text-foreground mb-6">Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <Input
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="+250 7XX XXX XXX"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <Textarea
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="How can we help you?"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="w-full rounded-full" data-testid="contact-submit-btn">
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="py-20 bg-muted/30" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#22c55e]/10 text-[#22c55e] rounded-full text-sm font-medium mb-3">Customer voices</span>
            <h2 className="font-heading font-bold text-4xl">What our customers say</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">Have you shopped with us? We'd love to hear how it went.</p>
          </div>

          {reviews.length > 0 && (
            <div className="grid md:grid-cols-3 gap-4 mb-10" data-testid="reviews-list">
              {reviews.slice(0, 6).map((r) => (
                <div key={r.id} className="p-5 bg-white rounded-2xl border border-border shadow-sm" data-testid={`review-card-${r.id}`}>
                  <div className="flex gap-0.5 mb-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-foreground italic leading-relaxed">"{r.comment}"</p>
                  <p className="text-xs font-medium text-muted-foreground mt-3">— {r.name}</p>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={submitReview} className="max-w-2xl mx-auto p-6 bg-white rounded-2xl border border-border shadow-sm space-y-4" data-testid="review-form">
            <h3 className="font-heading font-semibold text-xl text-center">Leave a review</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Your name *" value={reviewForm.name} onChange={(e) => setReviewForm(f => ({...f, name: e.target.value}))} required data-testid="review-name-input" />
              <Input type="email" placeholder="Email (optional)" value={reviewForm.email} onChange={(e) => setReviewForm(f => ({...f, email: e.target.value}))} data-testid="review-email-input" />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Rating</label>
              <div className="flex gap-1" data-testid="review-rating-picker">
                {[1,2,3,4,5].map(n => (
                  <button type="button" key={n} onClick={() => setReviewForm(f => ({...f, rating: n}))} data-testid={`review-star-${n}`}>
                    <Star className={`w-7 h-7 transition-transform hover:scale-110 ${n <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
                  </button>
                ))}
              </div>
            </div>
            <Textarea rows={4} placeholder="Tell us about your experience…" value={reviewForm.comment} onChange={(e) => setReviewForm(f => ({...f, comment: e.target.value}))} required data-testid="review-comment-input" />
            <Button type="submit" disabled={reviewSubmitting} className="w-full rounded-full" data-testid="review-submit-btn">{reviewSubmitting ? 'Submitting…' : 'Submit review'}</Button>
          </form>
        </div>
      </section>

      {/* Live Bison feed — LinkedIn + Instagram embeds + recent reviews (bottom) */}
      <BisonFeedCarousel />

      {/* Footer */}
      <footer className="bg-foreground text-white py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <button onClick={() => scrollToSection('hero')} className="flex items-center gap-3 hover:opacity-90 transition-opacity">
                <img 
                  src="/bison-logo.png" 
                  alt="Bison Food Solutions" 
                  className="h-12 w-auto"
                />
                <div className="text-left">
                  <h3 className="font-heading font-bold text-lg text-[#22c55e]">Bison Food Solutions Ltd.</h3>
                  <p className="text-sm text-white/70">Fresh, Reliable & Farmer-supported</p>
                </div>
              </button>
              <p className="mt-4 text-white/70 text-sm max-w-md">
                We don't just supply food — we build partnerships. Your trusted partner for premium fresh groceries in Rwanda.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-[#22c55e]">Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/70">
                {navLinks.map(link => (
                  <li key={link.id}>
                    <button onClick={() => scrollToSection(link.id)} className="hover:text-[#22c55e] transition-colors">
                      {link.label}
                    </button>
                  </li>
                ))}
                <li>
                  <button onClick={() => scrollToSection('contact')} className="hover:text-[#22c55e] transition-colors">
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-[#22c55e]">Contact</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li>
                  <a href="tel:+250791687658" className="flex items-center gap-2 hover:text-[#22c55e] transition-colors">
                    <Phone className="w-4 h-4 text-[#22c55e]" />
                    +250 791 687 658
                  </a>
                </li>
                <li>
                  <a href="mailto:sales@bison.rw" className="flex items-center gap-2 hover:text-[#22c55e] transition-colors">
                    <Mail className="w-4 h-4 text-[#22c55e]" />
                    sales@bison.rw
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/bison_food_solutions_ltd/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#E1306C] transition-colors">
                    <Instagram className="w-4 h-4 text-[#E1306C]" />
                    @bison_food_solutions_ltd
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/250791687658" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#25D366] transition-colors">
                    <MessageCircle className="w-4 h-4 text-[#25D366]" />
                    WhatsApp
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#22c55e]" />
                  Kigali, Rwanda
                </li>
              </ul>
              
              {/* Staff Login - Bottom Right */}
              <Button 
                onClick={() => window.location.href = '/login'} 
                variant="outline"
                className="mt-6 border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e]/10 rounded-full"
              >
                Staff Login
              </Button>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/10 text-center text-sm text-white/50">
            © {new Date().getFullYear()} Bison Food Solutions Ltd. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
    <FloatingWhatsapp />
    <RequestQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </>
  );
};

export default LandingPage;
